import { useCallback, useEffect, useMemo, useState } from "react";

import { Holding } from "../../../../services/holdings";
import { HOLDINGS_PAGE_SIZE } from "../components/Holdings/constants";
import { pageSlice } from "../components/Holdings/holdingsWorkspace";
import {
  HoldingSelection,
  HoldingsWorkspace,
} from "../components/Holdings/types";
import { useHoldingsWorkspace } from "../components/Holdings/useHoldingsWorkspace";

const CLEARED_ANNOUNCEMENT = "Position selection cleared.";

const FILTER_LOSS_ANNOUNCEMENT =
  "Selection cleared because that position is no longer in the filtered holdings.";

const PAGE_LOSS_ANNOUNCEMENT =
  "Selection cleared because that position is not on the visible holdings page.";

const selectionAnnouncementFor = (holding: Holding): string => {
  const { symbol } = holding.instrument;
  return `${symbol} selected. The position insight heading is now ${symbol} position insight.`;
};

/**
 * The holdings workspace plus the one position the contextual rail describes. Lives at the page
 * because both the workspace and the rail have to read the same rows.
 */
export const useLedgerWorkspace = (
  holdings: Holding[] | undefined,
): HoldingsWorkspace & HoldingSelection => {
  const workspace = useHoldingsWorkspace(holdings);
  const { filteredRows, setPage } = workspace;
  const [selectedHoldingId, setSelectedHoldingId] = useState<number | null>(
    null,
  );
  const [selectionAnnouncement, setSelectionAnnouncement] = useState("");

  const clearSelection = useCallback(() => {
    setSelectedHoldingId(null);
    setSelectionAnnouncement(CLEARED_ANNOUNCEMENT);
  }, []);

  const toggleHolding = useCallback((holding: Holding) => {
    setSelectedHoldingId((current) => {
      const isSelected = current === holding.id;
      setSelectionAnnouncement(
        isSelected ? CLEARED_ANNOUNCEMENT : selectionAnnouncementFor(holding),
      );
      return isSelected ? null : holding.id;
    });
  }, []);

  // `filteredRows` is memoised on the filters alone, so this follows a filter change and never
  // fights a page navigation — which is what lets `selectPage` own the page-caused clear below.
  useEffect(() => {
    if (selectedHoldingId === null) {
      return;
    }
    const index = filteredRows.findIndex((row) => row.id === selectedHoldingId);
    if (index === -1) {
      setSelectedHoldingId(null);
      setSelectionAnnouncement(FILTER_LOSS_ANNOUNCEMENT);
      return;
    }
    setPage(Math.floor(index / HOLDINGS_PAGE_SIZE));
  }, [filteredRows, selectedHoldingId, setPage]);

  const selectPage = useCallback(
    (nextPage: number) => {
      const stays = pageSlice(filteredRows, nextPage, HOLDINGS_PAGE_SIZE).some(
        (row) => row.id === selectedHoldingId,
      );
      if (selectedHoldingId !== null && !stays) {
        setSelectedHoldingId(null);
        setSelectionAnnouncement(PAGE_LOSS_ANNOUNCEMENT);
      }
      setPage(nextPage);
    },
    [filteredRows, selectedHoldingId, setPage],
  );

  const selectedHolding = useMemo(
    () => filteredRows.find((row) => row.id === selectedHoldingId) ?? null,
    [filteredRows, selectedHoldingId],
  );

  return useMemo(
    () => ({
      ...workspace,
      setPage: selectPage,
      selectedHoldingId,
      selectedHolding,
      selectionAnnouncement,
      toggleHolding,
      clearSelection,
    }),
    [
      workspace,
      selectPage,
      selectedHoldingId,
      selectedHolding,
      selectionAnnouncement,
      toggleHolding,
      clearSelection,
    ],
  );
};
